import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { platformTenancyService } from '@/services/platformTenancy.service';

const { Title, Text } = Typography;

const statusColor: Record<string, string> = {
  ACTIVE: 'green',
  DRAFT: 'default',
  PROVISIONING: 'processing',
  FAILED: 'error',
  SUSPENDED: 'orange',
  READ_ONLY: 'gold',
  ARCHIVED: 'default',
};

const PlatformCompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();

  const listQ = useQuery({
    queryKey: ['platform-tenants', search, status],
    queryFn: () =>
      platformTenancyService.listTenants({
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      }),
    staleTime: 15_000,
  });

  const runtimeQ = useQuery({
    queryKey: ['platform-tenancy-runtime'],
    queryFn: () => platformTenancyService.getRuntime(),
    staleTime: 60_000,
  });

  const kpis = listQ.data?.kpis || {};
  const tenants = listQ.data?.tenants || [];

  const columns = useMemo(
    () => [
      {
        title: 'Tenant',
        dataIndex: 'name',
        render: (_: unknown, row: any) => (
          <Space direction="vertical" size={0}>
            <Text strong>{row.display_name || row.name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.tenant_code}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: 130,
        render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s}</Tag>,
      },
      {
        title: 'Company',
        dataIndex: 'primary_company_name',
        render: (v: string, row: any) => v || <Text type="secondary">—</Text>,
      },
      {
        title: 'Plan',
        dataIndex: 'plan_code',
        width: 110,
        render: (v: string) => v || '—',
      },
      {
        title: 'Health',
        dataIndex: 'health_band',
        width: 110,
        render: (v: string, row: any) =>
          v ? `${v}${row.health_score != null ? ` (${row.health_score})` : ''}` : '—',
      },
      {
        title: 'Users',
        dataIndex: 'active_users',
        width: 80,
      },
      {
        title: 'Packs',
        dataIndex: 'installed_packs',
        width: 80,
            render: (packs: unknown[], row: any) =>
              typeof row.pack_count === 'number'
                ? row.pack_count
                : Array.isArray(packs)
                  ? packs.length
                  : 0,
      },
      {
        title: '',
        key: 'actions',
        width: 90,
        render: (_: unknown, row: any) => (
          <Button type="link" onClick={() => navigate(`/platform/companies/${row.id}`)}>
            Open
          </Button>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Platform Companies
          </Title>
          <Text type="secondary">
            Multi-tenant control plane — operational ERP stays company-scoped
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => listQ.refetch()}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/platform/companies/new')}>
            Create Tenant
          </Button>
        </Space>
      </Space>

      {listQ.isError ? (
        <Card size="small" style={{ marginBottom: 16, borderColor: '#ff4d4f' }}>
          <Text type="danger">
            Failed to load platform tenants. {(listQ.error as Error)?.message || 'Check API / permissions and retry.'}
          </Text>
        </Card>
      ) : null}

      {runtimeQ.data?.provisioningDisabled ? (
        <Card size="small" style={{ marginBottom: 16, borderColor: '#faad14' }}>
          <Text type="warning">PLATFORM_TENANT_PROVISIONING_DISABLED is on — provisioning APIs return 503.</Text>
        </Card>
      ) : null}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Total" value={Number((kpis as any).total || tenants.length)} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Active" value={Number((kpis as any).active || 0)} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Provisioning" value={Number((kpis as any).provisioning || 0)} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic title="Failed" value={Number((kpis as any).failed || 0)} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            allowClear
            placeholder="Search code, name, contact…"
            style={{ width: 280 }}
            onSearch={(v) => setSearch(v.trim())}
            onChange={(e) => {
              if (!e.target.value) setSearch('');
            }}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={(v) => setStatus(v)}
            options={['ACTIVE', 'DRAFT', 'PROVISIONING', 'FAILED', 'SUSPENDED', 'ARCHIVED'].map((s) => ({
              value: s,
              label: s,
            }))}
          />
        </Space>
        <Table
          rowKey="id"
          loading={listQ.isLoading}
          dataSource={tenants}
          columns={columns as any}
          pagination={{ pageSize: 20 }}
          onRow={(row) => ({
            onClick: () => navigate(`/platform/companies/${row.id}`),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: 'No platform tenants yet' }}
        />
      </Card>
    </div>
  );
};

export default PlatformCompaniesPage;
